import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useGroups, useConfig, useIntakeRecord, useWorkshopServiceAdvisors } from "../lib/api";
import { SERVICE_TYPES, DOCUMENT_ACCEPT, fileMatchesAccept } from "../lib/journey";
import { uploadBookingDocs } from "../lib/bookingDocs";
import { PickupDropChoice } from "../components/journey/PickupDropChoice";
import { useAuth } from "../lib/AuthContext";
import { useTenantTerms } from "../lib/TenantConfigContext";
import { Loader2 } from "lucide-react";
import { Icon } from "../components/msd/Icon";
import { Btn, Page, PageHead, cx } from "../components/msd/ui";
import HyundaiBookService from "./HyundaiBookService";
import MarutiBookService from "./MarutiBookService";
import { useBookingFlow } from "../lib/useBookingFlow";
import BookingProgress from "../components/booking/BookingProgress";
import { UP_ROW, upIconCls } from "../components/booking/UploadSlot";
import {
  BookingLayout,
  FORM_CARD,
  FormField as Field,
  GroupPreview,
  HINT,
  ROW2,
  WizardFoot,
  bookingMembers,
  deliveryLabel,
  inputCls,
} from "../components/booking/WizardParts";
import Dropdown from "../components/Dropdown";

// A value the QR already answered: shown in a field's place, but not editable.
const READ_ONLY = "flex min-h-[46px] items-center break-words rounded-xl bg-msd-ground px-[14px] py-[10px] text-[16px] text-msd-ink";

const ReadOnlyField = ({ label, value }) => (
  <Field group label={label}>
    <div className={READ_ONLY}>{value || <span className="text-msd-faint">—</span>}</div>
  </Field>
);

// The booking's optional inventory photos/videos, estimate and job card.
// Nothing is sent now: the inventory and the estimate go to the customer when
// they tap Let's get started, and the job card once they approve the estimate.
const MEDIA_ACCEPT = "image/*,video/*";
const DOCUMENT_KINDS = "PDF, Word, Excel, CSV, text or HTML files";
const ACCEPTED = {
  estimate: [DOCUMENT_ACCEPT, DOCUMENT_KINDS],
  jobCard: [DOCUMENT_ACCEPT, DOCUMENT_KINDS],
  media: [MEDIA_ACCEPT, "images or videos"],
};

function BookingDocumentsField({ docs, onChange }) {
  const pick = (key, label, multiple) => (e) => {
    const picked = Array.from(e.target.files || []);
    const [accept, kinds] = ACCEPTED[key];
    if (picked.some((file) => !fileMatchesAccept(file, accept))) {
      toast.error(`${label} accepts ${kinds} only.`);
      e.target.value = "";
      return;
    }
    onChange({ ...docs, [key]: multiple ? picked : picked[0] || null });
  };
  const row = (key, label, { multiple = false } = {}) => {
    const chosen = multiple ? docs[key] || [] : docs[key] ? [docs[key]] : [];
    return (
      <label className={UP_ROW}>
        <span className={upIconCls(chosen.length > 0)}>
          <Icon name={chosen.length ? "check" : multiple ? "img" : "doc"} size={18} stroke={2.2} />
        </span>
        <span className="min-w-0 grow">
          <b className="block text-[15px] font-semibold">{label}</b>
          <span className="block truncate text-[13px] text-msd-muted">
            {chosen.length > 1 ? `${chosen.length} files chosen` : chosen[0]?.name || (multiple ? "Choose files" : "Choose a file")}
          </span>
        </span>
        <input
          type="file"
          accept={ACCEPTED[key][0]}
          multiple={multiple}
          className="hidden"
          data-testid={`booking-doc-${key}`}
          onChange={pick(key, label, multiple)}
        />
      </label>
    );
  };
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-[13.5px] font-semibold text-msd-ink2">
          Documents <span className="font-normal text-msd-muted">optional</span>
        </div>
        <p className={cx(HINT, "m-0 mt-1")}>
          The inventory and the estimate are sent when the customer taps Let&apos;s get started, and the job card once
          they approve the estimate.
        </p>
      </div>
      {row("media", "Inventory photos / videos", { multiple: true })}
      {row("estimate", "Estimate")}
      {row("jobCard", "Job card")}
    </div>
  );
}

// Native <input type="date"> lets Chrome keep typing year digits past 4 unless min/max
// clamp the range. Keep delivery dates within a practical workshop window.
function deliveryTimelineBounds() {
  const today = new Date();
  const max = new Date(today);
  max.setFullYear(today.getFullYear() + 5);
  const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { min: iso(today), max: iso(max) };
}

export function isValidDeliveryTimeline(value) {
  if (!value) return false;
  // Strict YYYY-MM-DD with a 4-digit year (rejects 275760-style overflow)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const { min, max } = deliveryTimelineBounds();
  return value >= min && value <= max;
}

// 18:00 — the hour the backfill assumed for rows booked before the time field
// existed, so a booking made today and one made last week mean the same thing.
export const DEFAULT_DELIVERY_TIME = "18:00";

export function isValidDeliveryTime(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value || "");
}

// The date input's `min` only guards the date, so "today at 9am" entered at 3pm
// passes it. A promise already in the past is a breach the moment it's saved.
export function isDeliveryPromiseInPast(date, time) {
  if (!isValidDeliveryTimeline(date) || !isValidDeliveryTime(time)) return false;
  const at = new Date(`${date}T${time}`);
  return !Number.isNaN(at.getTime()) && at.getTime() <= Date.now();
}

// Combines the picked date and time into one absolute instant, as ISO 8601 in
// UTC. The SLA sweeper compares this against server time — a bare local-time
// string with no offset would put every breach hours out.
export function toPromisedDeliveryISO(date, time) {
  if (!isValidDeliveryTimeline(date) || !isValidDeliveryTime(time)) return "";
  const at = new Date(`${date}T${time}`);
  return Number.isNaN(at.getTime()) ? "" : at.toISOString();
}

const DeliveryPromiseInput = ({ date, time, onChange }) => {
  const { min, max } = deliveryTimelineBounds();
  return (
    <div className={ROW2}>
      <input
        type="date"
        data-testid="input-delivery-timeline"
        className={inputCls}
        min={min}
        max={max}
        value={date}
        onChange={(e) => {
          const next = e.target.value;
          // Allow clearing; ignore oversized / out-of-range years while typing
          if (!next || isValidDeliveryTimeline(next)) {
            // Picking a date with no time yet fills in the default, so the
            // common case is one interaction rather than two.
            onChange({ date: next, time: next ? time || DEFAULT_DELIVERY_TIME : "" });
          }
        }}
      />
      <input
        type="time"
        step={1800}
        data-testid="input-delivery-time"
        aria-label="Delivery time"
        className={inputCls}
        value={time}
        onChange={(e) => onChange({ date, time: e.target.value })}
      />
    </div>
  );
};

const SubmitButton = ({ pending }) => (
  <Btn type="submit" variant="pri" icon={pending ? undefined : "wa"} data-testid="submit-create-group" disabled={pending}>
    {pending ? (
      <>
        <Loader2 className="animate-spin" size={16} /> Saving…
      </>
    ) : (
      "Book Service"
    )}
  </Btn>
);

// Formats service_history (jsonb - shape not guaranteed) into a short readable list,
// falling back to raw JSON for anything unexpected rather than crashing on it.
function ServiceHistorySummary({ history }) {
  let items = history;
  if (typeof items === "string") {
    try {
      items = JSON.parse(items);
    } catch {
      return <div className={READ_ONLY}>{items}</div>;
    }
  }
  if (!items || (Array.isArray(items) && items.length === 0)) {
    return <div className={cx(READ_ONLY, "!text-msd-faint")}>No prior service history</div>;
  }
  if (!Array.isArray(items)) {
    return (
      <pre className="m-0 overflow-x-auto whitespace-pre-wrap rounded-xl bg-msd-ground px-[14px] py-3 text-[12.5px] text-msd-ink">
        {JSON.stringify(items, null, 2)}
      </pre>
    );
  }
  return (
    <div className="divide-y divide-msd-line rounded-xl bg-msd-ground">
      {items.map((entry, i) => (
        <div key={i} className="break-words px-[14px] py-[10px] text-[14.5px] text-msd-ink">
          {typeof entry === "object" && entry !== null
            ? Object.entries(entry)
                .map(([k, v]) => `${k}: ${v}`)
                .join(" · ")
            : String(entry)}
        </div>
      ))}
    </div>
  );
}

// Pre-filled flow: the customer/vehicle details came from a scanned QR (intake_records),
// shown read-only. The SA's one job here is assignment — Workshop Location + Service
// Advisor + Service Type (the last is required to create a group and has no source in the
// intake record, so it stays a real field rather than a guessed default).
function TokenBookingForm({ token, record }) {
  const { roleLabel } = useTenantTerms();
  const { me } = useAuth();
  const { data: cfg } = useConfig();
  const { data: advisorsData } = useWorkshopServiceAdvisors();
  useGroups(); // warm cache
  const flow = useBookingFlow();
  const [docs, setDocs] = useState({ estimate: null, jobCard: null, media: [] });
  const allConfigLocations = (cfg?.rows || []).map((r) => r["Workshop Location"]).filter(Boolean);

  const isServiceAdvisor = me?.role === "sa";
  const isSingleLocationLocked = me?.role && ["sa", "ccm", "wm", "psf", "receptionist"].includes(me.role);
  const isGm = me?.role === "gm";
  const userWorkshopLocation = me?.workshop_location;
  const gmLocations = useMemo(
    () => (Array.isArray(me?.workshop_locations) ? me.workshop_locations.filter(Boolean) : []),
    [me]
  );
  const locations = isGm
    ? gmLocations
    : isSingleLocationLocked && userWorkshopLocation
      ? [userWorkshopLocation]
      : allConfigLocations;
  const userServiceAdvisorId = me?.service_advisor_id;
  const userName = me?.name;
  const userPhone = me?.phone;

  const initialLocation = isSingleLocationLocked
    ? userWorkshopLocation || record.workshop_location || ""
    : isGm && gmLocations.length === 1
      ? gmLocations[0]
      : record.workshop_location || "";

  const [f, setF] = useState({
    workshop_location: initialLocation,
    service_advisor_id: isServiceAdvisor ? String(userServiceAdvisorId || "") : "",
    service_advisor_name: isServiceAdvisor ? userName || "" : "",
    service_advisor_number: isServiceAdvisor ? userPhone || "" : "",
    service_type: "",
    pickup_drop: "",
    delivery_timeline: "",
    delivery_time: "",
  });

  useEffect(() => {
    const updates = {};
    if (isSingleLocationLocked && userWorkshopLocation && f.workshop_location !== userWorkshopLocation) {
      updates.workshop_location = userWorkshopLocation;
    }
    if (isGm && gmLocations.length === 1 && !f.workshop_location) {
      updates.workshop_location = gmLocations[0];
    }
    if (isServiceAdvisor) {
      if (userServiceAdvisorId && !f.service_advisor_id) {
        updates.service_advisor_id = String(userServiceAdvisorId);
      }
      if (userName && !f.service_advisor_name) {
        updates.service_advisor_name = userName;
      }
      if (userPhone && !f.service_advisor_number) {
        updates.service_advisor_number = userPhone;
      }
    }
    if (Object.keys(updates).length > 0) {
      setF((s) => ({ ...s, ...updates }));
    }
  }, [isServiceAdvisor, isSingleLocationLocked, isGm, userWorkshopLocation, gmLocations, userServiceAdvisorId, userName, userPhone, f.workshop_location, f.service_advisor_id, f.service_advisor_name, f.service_advisor_number]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const advisorsForLocation = (advisorsData?.rows || []).filter(
    (a) => a.workshop_location === f.workshop_location
  );

  const setLocation = (e) => {
    const workshop_location = e.target.value;
    setF((s) => ({ ...s, workshop_location, service_advisor_id: "", service_advisor_name: "", service_advisor_number: "" }));
  };

  const setAdvisor = (e) => {
    const id = e.target.value;
    const advisor = advisorsForLocation.find((a) => String(a.id) === id);
    setF((s) => ({
      ...s,
      service_advisor_id: id,
      service_advisor_name: advisor?.name || "",
      service_advisor_number: advisor?.number || "",
    }));
  };

  const groupName = record.vehicle_registration_number || record.vehicle_model || record.customer_name;

  const submit = (e) => {
    e.preventDefault();
    if (!f.workshop_location || !f.service_type || !f.pickup_drop || (f.delivery_timeline && !isValidDeliveryTimeline(f.delivery_timeline))) {
      toast.error("Select a Workshop Location, Service Type, and Pick & Drop or Self Visit. A promised delivery date, if set, must be valid.");
      return;
    }
    // Required: without an advisor nobody is invited to the group on their
    // behalf, and the job card never appears in their desk.
    if (!f.service_advisor_number) {
      toast.error("Select a service advisor — they are invited to the group and own this job card.");
      return;
    }
    if (f.delivery_timeline && !isValidDeliveryTime(f.delivery_time)) {
      toast.error("Pick a promised delivery time.");
      return;
    }
    if (isDeliveryPromiseInPast(f.delivery_timeline, f.delivery_time)) {
      toast.error("That delivery time has already passed. Pick a later one.");
      return;
    }
    // Uploads, saving, the group, the invitations and opening the desk all happen
    // behind the progress screen; the desk opens only once the invites are out.
    flow.start({
      prepare: () => uploadBookingDocs(docs, { customerName: record.customer_name, vehicle: groupName }),
      payload: {
        group_name: groupName,
        description: record.vehicle_model || "",
        customer_name: record.customer_name,
        customer_number: record.customer_number,
        workshop_location: f.workshop_location,
        service_advisor_name: f.service_advisor_name,
        service_advisor_number: f.service_advisor_number,
        service_type: f.service_type,
        pickup_drop: f.pickup_drop,
        delivery_timeline: f.delivery_timeline,
        promised_delivery_at: toPromisedDeliveryISO(f.delivery_timeline, f.delivery_time),
        intake_token: token,
      },
    });
  };

  return (
    <BookingLayout
      sub="Customer and vehicle details from the scanned QR. Confirm the assignment below."
      preview={
        <GroupPreview
          name={groupName}
          sub={record.vehicle_model}
          meta={[
            ["Workshop", f.workshop_location || "Not chosen"],
            ["Work", f.service_type || "Not chosen"],
            ["Delivery", deliveryLabel(f.delivery_timeline, f.delivery_time)],
          ]}
          members={bookingMembers({
            customerName: record.customer_name,
            customerPhone: record.customer_number,
            advisorName: f.service_advisor_name || f.service_advisor_number,
            advisorRole: roleLabel("sa"),
            hasWorkshop: !!f.workshop_location,
          })}
        />
      }
    >
      <div className="flex min-w-0 flex-col gap-[18px]">
        {flow.active && (
          <div className={FORM_CARD}>
            <BookingProgress flow={flow} hasUploads />
          </div>
        )}

        <form onSubmit={submit} className={cx(FORM_CARD, flow.active && "hidden")}>
          <div className={ROW2}>
            <ReadOnlyField label="Customer name" value={record.customer_name} />
            <ReadOnlyField label="Customer number" value={record.customer_number} />
          </div>
          <div className={ROW2}>
            <ReadOnlyField label="Vehicle model" value={record.vehicle_model} />
            <ReadOnlyField label="Vehicle registration number" value={record.vehicle_registration_number} />
          </div>
          <Field group label="Service history">
            <ServiceHistorySummary history={record.service_history} />
          </Field>

          <div className="flex flex-col gap-[18px] pt-[18px] shadow-[0_-1px_0_var(--line)]">
            <Field label="Workshop location" required>
              <Dropdown
                data-testid="select-location"
                className={inputCls}
                value={f.workshop_location}
                onChange={setLocation}
                disabled={isSingleLocationLocked}
              >
                <option value="">Select location…</option>
                {locations.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </Dropdown>
            </Field>
            <Field label={roleLabel("sa")}>
              {isServiceAdvisor ? (
                <input
                  data-testid="input-service-advisor"
                  className={inputCls}
                  value={f.service_advisor_name || f.service_advisor_number || "You"}
                  disabled
                />
              ) : (
                <Dropdown
                  data-testid="select-service-advisor"
                  className={inputCls}
                  value={f.service_advisor_id}
                  onChange={setAdvisor}
                  disabled={!f.workshop_location}
                >
                  <option value="">
                    {f.workshop_location ? "Select service advisor…" : "Select a workshop location first"}
                  </option>
                  {advisorsForLocation.map((a) => (
                    <option key={a.id} value={a.id}>{a.name || a.number}{a.number ? ` (${a.number})` : ""}</option>
                  ))}
                </Dropdown>
              )}
            </Field>
            <Field label="Service type" required>
              <Dropdown data-testid="select-service-type" className={inputCls} value={f.service_type} onChange={set("service_type")}>
                <option value="">Select service type…</option>
                {SERVICE_TYPES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Dropdown>
            </Field>
            <Field group label="Pick & drop" required>
              <PickupDropChoice value={f.pickup_drop} onChange={(v) => setF((s) => ({ ...s, pickup_drop: v }))} />
            </Field>
            <Field group label="Promised delivery" hint="Optional">
              <DeliveryPromiseInput
                date={f.delivery_timeline}
                time={f.delivery_time}
                onChange={({ date, time }) =>
                  setF((s) => ({ ...s, delivery_timeline: date, delivery_time: time }))
                }
              />
            </Field>
          </div>

          <BookingDocumentsField docs={docs} onChange={setDocs} />

          <WizardFoot>
            <span />
            <SubmitButton pending={flow.active} />
          </WizardFoot>
        </form>
      </div>
    </BookingLayout>
  );
}

function InvalidTokenNotice() {
  return (
    <Page data-testid="create-group-page">
      <PageHead title="Book a service" />
      <div className="flex max-w-[720px] items-start gap-3 rounded-2xl bg-msd-surface px-4 py-[14px] shadow-msd-e1">
        <Icon name="warn" size={18} stroke={2} color="#F79009" className="mt-[2px] shrink-0" />
        <div>
          <div className="text-[15px] font-semibold text-msd-ink">This QR code is no longer valid</div>
          <p className="m-0 mt-1 text-[14px] text-msd-muted">
            It may have already been used or expired. Please generate a new QR code for this customer, or{" "}
            <Link to="/create" className="font-semibold text-msd-royal">
              start a manual booking instead
            </Link>
            .
          </p>
        </div>
      </div>
    </Page>
  );
}

export default function CreateGroup() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { bookServiceUi } = useTenantTerms();
  const { data: record, isLoading, isError } = useIntakeRecord(token);

  // ponytail: Hyundai UI only applies to manual bookings (no QR token).
  if (!token && bookServiceUi === "hyundai") return <HyundaiBookService />;

  if (!token) return <MarutiBookService />;

  if (isLoading) {
    return (
      <Page data-testid="create-group-page">
        <div className="flex items-center gap-2 text-[15px] text-msd-muted" data-testid="intake-loading">
          <Loader2 className="animate-spin" size={18} /> Loading customer details…
        </div>
      </Page>
    );
  }

  if (isError || !record) return <InvalidTokenNotice />;

  return <TokenBookingForm token={token} record={record} />;
}
